import {
  Users,
  UserCheck,
  FileCheck,
  Download,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDashboardCandidates } from "../hooks/useDashboard";

interface KpiItem {
  id: string;
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
}

function KpiCard({ kpi, loading }: { kpi: KpiItem; loading: boolean }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group flex flex-col">
      <div
        className={cn(
          "absolute bottom-0 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity",
          kpi.bg.replace("/10", "/50").replace("50", "500"),
        )}
      />

      <div className="flex items-start justify-between mb-4">
        <div className={cn("p-2 rounded-xl", kpi.bg)}>
          <kpi.icon className={cn("h-5 w-5", kpi.color)} />
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
        {loading ? (
          <div className="h-8 w-20 rounded-md bg-slate-200 dark:bg-slate-700 animate-pulse mt-1" />
        ) : (
          <h3 className="text-2xl font-bold text-foreground mt-1">{kpi.value}</h3>
        )}
      </div>
    </div>
  );
}

export function KpiGrid() {
  const { data, isLoading } = useDashboardCandidates();
  const apiData = data?.data;

  const kpis: KpiItem[] = [
    {
      id: "total-candidates",
      title: "Total Candidates",
      value: apiData?.total_candidates != null ? String(apiData.total_candidates) : "—",
      icon: Users,
      color: "text-indigo-500",
      bg: "bg-indigo-50 dark:bg-indigo-500/10",
    },
    {
      id: "active-candidates",
      title: "Active Candidates",
      value: apiData?.active_candidates != null ? String(apiData.active_candidates) : "—",
      icon: UserCheck,
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-500/10",
    },
    {
      id: "resume-completion",
      title: "Avg. Completion",
      value: apiData?.avg_completion_percentage != null
        ? `${apiData.avg_completion_percentage}%`
        : "—",
      icon: FileCheck,
      color: "text-emerald-500",
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
    },
    {
      id: "total-exports",
      title: "Total Exports",
      value: apiData?.total_exports != null ? String(apiData.total_exports) : "—",
      icon: Download,
      color: "text-amber-500",
      bg: "bg-amber-50 dark:bg-amber-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi) => (
        <KpiCard key={kpi.id} kpi={kpi} loading={isLoading} />
      ))}
    </div>
  );
}
